import { FC, ReactNode } from 'react'
import { LuCalendarDays } from 'react-icons/lu'

interface ISection {
    title: string
    subtitle?: ReactNode
    /** Active date range echo, e.g. `01/09/2026 – 30/09/2026` */
    range?: string
    actions?: ReactNode
    children: ReactNode
    className?: string
    bodyClassName?: string
}

export const RangeBadge: FC<{ range: string }> = ({ range }) => (
    <span className='pill gap-1.5 bg-primary-50 text-primary-800 tabular-nums'>
        <LuCalendarDays aria-hidden className='h-3.5 w-3.5' />
        {range}
    </span>
)

/** Card with a header (title / subtitle / actions) and a padded body. */
export const Section: FC<ISection> = ({ title, subtitle, range, actions, children, className = '', bodyClassName = '' }) => (
    <section className={`card min-w-0 ${className}`}>
        <div className='flex flex-wrap items-start justify-between gap-3 border-b border-line-soft px-4 py-4 sm:px-6'>
            <div className='min-w-0'>
                <h3 className='text-h3 text-ink'>{title}</h3>
                {subtitle && <p className='mt-0.5 text-sm text-ink-faint'>{subtitle}</p>}
            </div>
            {(actions || range) && <div className='flex flex-wrap items-center gap-2'>
                {range && <RangeBadge range={range} />}
                {actions}
            </div>}
        </div>
        <div className={`p-4 sm:p-6 ${bodyClassName}`}>{children}</div>
    </section>
)

interface ISectionHeading {
    title: string
    description?: ReactNode
    range?: string
    children?: ReactNode
}

/** Heading for a group of cards (h2). */
export const SectionHeading: FC<ISectionHeading> = ({ title, description, range, children }) => (
    <div className='flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
            <h2 className='text-h2 text-ink'>{title}</h2>
            {description && <p className='mt-1 text-sm text-ink-muted'>{description}</p>}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
            {range && <RangeBadge range={range} />}
            {children}
        </div>
    </div>
)

/** Fixed-height chart container (responsive). */
export const ChartBox: FC<{ children: ReactNode, tall?: boolean }> = ({ children, tall }) => (
    <div className={`w-full min-w-0 ${tall ? 'h-[280px] sm:h-[340px]' : 'h-[240px] sm:h-[300px]'}`}>{children}</div>
)
